import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Wallet, Landmark, Building2, CheckCircle,
    Loader2, ChevronDown, RotateCcw, Send, ArrowLeftRight, Layers,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchCCCashBalance,
    fetchCashAmounts,
    submitCentralDayBook,
    clearCentralDayBookSaveResult,
    resetCentralDayBook,
    selectCCBalanceList,
    selectCCBalanceListLoading,
    selectCashAmounts,
    selectCashAmountsLoading,
    selectCentralDayBookSaveStatus,
    selectCentralDayBookSaveLoading,
    selectCentralDayBookSaveError,
} from '../../slices/accountsSlice/centralDayBookSlice';

import {
    fetchBankDetailsWithAvailableBalance,
    resetBankDetailsData,
    selectBankDetailsArray,
    selectBankDetailsLoading,
} from '../../slices/CommonSlice/bankDetailsSlice';

// ── Constants ──────────────────────────────────────────────────────────────────
const TRANSFER_TYPE_OPTIONS = [
    { value: 'CostCenter', label: 'Cost Center', icon: Building2, description: 'Distribute cash to a cost centre' },
    { value: 'Bank',       label: 'Bank',         icon: Landmark,  description: 'Return cash back to a bank' },
];

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── Shared UI primitives (matches Refund / RecieptAgainstScrapSale) ────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const selectCls = `${inputCls} appearance-none pr-8 cursor-pointer`;

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

const BalanceTile = ({ label, value, accent }) => (
    <div className={`rounded-lg px-3 py-2 border ${accent}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
        <p className="text-lg font-bold">₹ {fmt(value)}</p>
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────
const CentralDayBook = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Selectors ──────────────────────────────────────────────────────────────
    const ccBalanceList  = useSelector(selectCCBalanceList);
    const ccListLoading  = useSelector(selectCCBalanceListLoading);
    const cashAmounts    = useSelector(selectCashAmounts);
    const cashAmtLoading = useSelector(selectCashAmountsLoading);

    const bankList    = useSelector(selectBankDetailsArray);
    const bankLoading = useSelector(selectBankDetailsLoading);

    const saveStatus = useSelector(selectCentralDayBookSaveStatus);
    const saving     = useSelector(selectCentralDayBookSaveLoading);
    const saveError  = useSelector(selectCentralDayBookSaveError);

    // ── Form state ─────────────────────────────────────────────────────────────
    const [date,         setDate]         = useState(() => new Date());
    const [transferType, setTransferType] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [selectedCC,   setSelectedCC]   = useState('');
    const [amount,       setAmount]       = useState('');
    const [remarks,      setRemarks]      = useState('');

    const isBusy = saving;

    // ── Init / refresh ────────────────────────────────────────────────────────
    const loadBalances = () => {
        if (userId && roleId) dispatch(fetchCCCashBalance({ roleId, uid: userId }));
        dispatch(fetchCashAmounts());
    };

    useEffect(() => {
        loadBalances();
        dispatch(fetchBankDetailsWithAvailableBalance());
        return () => {
            dispatch(resetCentralDayBook());
            dispatch(resetBankDetailsData());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userId, roleId]);

    // ── Save result ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Central day book entry saved successfully!');
            dispatch(clearCentralDayBookSaveResult());
            handleReset();
            loadBalances();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearCentralDayBookSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus, saveError, dispatch]);

    // ── Transfer type change ──────────────────────────────────────────────────
    const handleTransferTypeChange = (val) => {
        setTransferType(val);
        setSelectedBank('');
        setSelectedCC('');
    };

    // ── Reset ──────────────────────────────────────────────────────────────────
    const handleReset = () => {
        setDate(new Date());
        setTransferType('');
        setSelectedBank('');
        setSelectedCC('');
        setAmount('');
        setRemarks('');
    };

    // ── Derived ────────────────────────────────────────────────────────────────
    const selectedCCObj   = ccBalanceList.find((c) => c.CCCode === selectedCC);
    const selectedBankObj = bankList.find((b) => String(b.BankId) === String(selectedBank));

    const canSubmit = date && transferType &&
        (transferType === 'Bank' ? !!selectedBank : !!selectedCC) &&
        parseFloat(amount) > 0 && remarks.trim();

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!date)                          { toast.warn('Please select a date.');                    return; }
        if (!transferType)                  { toast.warn('Please select Transfer To — Bank or Cost Center.'); return; }
        if (transferType === 'Bank' && !selectedBank)     { toast.warn('Please select a bank.');       return; }
        if (transferType === 'CostCenter' && !selectedCC) { toast.warn('Please select a cost centre.'); return; }
        if (!(parseFloat(amount) > 0))      { toast.warn('Please enter a valid amount.');              return; }
        if (!remarks.trim())                { toast.warn('Please enter remarks.');                     return; }

        dispatch(submitCentralDayBook({
            transferDate: formatDateForAPI(date),
            transferType,
            bankId:       selectedBank,
            ccCode:       selectedCC,
            amount:       parseFloat(amount) || 0,
            remarks:      remarks.trim(),
            createdBy:    userName,
            roleId,
        }));
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <ArrowLeftRight className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Central Day Book'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Distribute cash drawn from bank to cost centres, or return it back to bank</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-4">

                {/* ── Balance Overview — always visible ────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Wallet} title="Balance Overview" action={
                        <button onClick={loadBalances} disabled={cashAmtLoading || ccListLoading}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                            <RotateCcw className={`h-3.5 w-3.5 ${cashAmtLoading || ccListLoading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                    } />
                    <div className="p-3">
                        {cashAmtLoading ? (
                            <div className="flex items-center gap-2 px-2 py-2 text-sm text-blue-700 dark:text-blue-400">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading balances…
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <BalanceTile
                                    label="Unassigned"
                                    value={cashAmounts?.Unassignedbalance}
                                    accent="bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300"
                                />
                                <BalanceTile
                                    label="With Cost Centres"
                                    value={cashAmounts?.Allccbalance}
                                    accent="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                                />
                                <BalanceTile
                                    label="Debit Pending"
                                    value={cashAmounts?.Pendingbalance}
                                    accent="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                    {/* ── Transfer entry — all fields in one card ──────────── */}
                    <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={Layers} title="Transfer Entry" />
                        <div className="p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                <div>
                                    <Label required>Date</Label>
                                    <CustomDatePicker
                                        value={date}
                                        onChange={setDate}
                                        disabled={isBusy}
                                        maxDate={new Date()}
                                        placeholder="Select date"
                                    />
                                </div>
                                <div>
                                    <Label required>Transfer To</Label>
                                    <div className="flex gap-2">
                                        {TRANSFER_TYPE_OPTIONS.map((t) => (
                                            <button
                                                key={t.value}
                                                type="button"
                                                onClick={() => !isBusy && handleTransferTypeChange(t.value)}
                                                disabled={isBusy}
                                                title={t.description}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-semibold transition-all
                                                    ${transferType === t.value
                                                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 shadow-sm'
                                                        : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 bg-white dark:bg-gray-800'}
                                                    disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                <t.icon className="h-4 w-4" />
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Destination — bank or cost centre */}
                                <div>
                                    <Label required>{transferType === 'Bank' ? 'Bank' : transferType === 'CostCenter' ? 'Cost Centre' : 'Destination'}</Label>
                                    <div className="relative">
                                        {transferType === 'Bank' ? (
                                            <select
                                                value={selectedBank}
                                                onChange={(e) => setSelectedBank(e.target.value)}
                                                disabled={bankLoading || isBusy}
                                                className={selectCls}
                                            >
                                                <option value="">{bankLoading ? 'Loading…' : '— Select Bank —'}</option>
                                                {bankList.map((b) => (
                                                    <option key={b.BankId} value={b.BankId}>{b.BankName}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <select
                                                value={selectedCC}
                                                onChange={(e) => setSelectedCC(e.target.value)}
                                                disabled={!transferType || ccListLoading || isBusy}
                                                className={selectCls}
                                            >
                                                <option value="">
                                                    {!transferType ? 'Select Transfer To first' : ccListLoading ? 'Loading…' : '— Select Cost Centre —'}
                                                </option>
                                                {ccBalanceList.map((cc) => (
                                                    <option key={cc.CCCode} value={cc.CCCode}>{cc.CCCode} — {cc.CCName}</option>
                                                ))}
                                            </select>
                                        )}
                                        <SelectIcon loading={transferType === 'Bank' ? bankLoading : ccListLoading} />
                                    </div>
                                    {(selectedBankObj || selectedCCObj) && (
                                        <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                                            {transferType === 'Bank' ? 'Available Balance' : 'Current CC Balance'}:{' '}
                                            ₹ {fmt(transferType === 'Bank' ? selectedBankObj?.AvailableBalance : selectedCCObj?.CCAmount)}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <Label required>Amount (₹)</Label>
                                    <input
                                        type="number" min="0" step="0.01" placeholder="0.00"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        disabled={isBusy}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            <div>
                                <Label required>Remarks</Label>
                                <textarea
                                    rows={2}
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    disabled={isBusy}
                                    placeholder="Enter remarks…"
                                    className={`${inputCls} resize-y`}
                                />
                            </div>

                            {/* Footer — inline summary + actions */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-400 min-w-0 truncate">
                                    {canSubmit ? (
                                        <>
                                            <CheckCircle className="inline h-3.5 w-3.5 text-emerald-500 mr-1 -mt-0.5" />
                                            <span className="font-semibold text-gray-700 dark:text-gray-200">₹ {fmt(amount)}</span>
                                            {' '}to{' '}
                                            <span className="font-semibold text-gray-700 dark:text-gray-200">
                                                {transferType === 'Bank' ? selectedBankObj?.BankName : (selectedCCObj ? `${selectedCCObj.CCCode} — ${selectedCCObj.CCName}` : selectedCC)}
                                            </span>
                                            {' '}on {formatDateForAPI(date)}
                                        </>
                                    ) : 'Fill all required fields to submit'}
                                </p>
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        disabled={isBusy}
                                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <RotateCcw className="h-4 w-4" /> Reset
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isBusy}
                                        className="flex items-center justify-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-semibold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? (
                                            <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
                                        ) : (
                                            <><Send className="h-4 w-4" /> Submit</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── All Cost Centre Balances ─────────────────────────── */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={Building2} title="Cost Centre Balances" />
                        <div className="p-3">
                            {ccListLoading ? (
                                <div className="flex items-center gap-2 px-2 py-2 text-sm text-blue-700 dark:text-blue-400">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading cost centre balances…
                                </div>
                            ) : ccBalanceList.length === 0 ? (
                                <p className="text-sm text-gray-400 dark:text-gray-500 px-2 py-2">No cost centre balances found.</p>
                            ) : (
                                <div className="overflow-auto max-h-[420px] rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0">
                                            <tr className="bg-gray-50 dark:bg-gray-900">
                                                <th className="px-3 py-2 text-left font-bold text-gray-500 dark:text-gray-400 uppercase text-[11px]">Cost Centre</th>
                                                <th className="px-3 py-2 text-right font-bold text-gray-500 dark:text-gray-400 uppercase text-[11px]">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ccBalanceList.map((cc) => (
                                                <tr
                                                    key={cc.CCCode}
                                                    className={`border-t border-gray-100 dark:border-gray-700 ${selectedCC === cc.CCCode ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
                                                >
                                                    <td className="px-3 py-1.5 min-w-0">
                                                        <p className="font-medium text-gray-700 dark:text-gray-200">{cc.CCCode}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]" title={cc.CCName}>{cc.CCName}</p>
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">₹ {fmt(cc.CCAmount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CentralDayBook;
